import { Component, Event, EventEmitter, h, Listen, Prop, State, Watch } from '@stencil/core';
import { AiIconType } from '../../utils/icon-types';

export interface SearchResult {
  title: string;
  fragment: string;
  url: string;
  page_title?: string;
}

interface GroupedResult {
  url: string;
  title: string;
  results: SearchResult[];
}

@Component({
  tag: 'biel-search-body',
  styleUrl: 'biel-search-body.css',
  shadow: true,
})
export class BielSearchBody {
  @Prop() hideAskAiButton: boolean = false;
  @Prop() searchResults: SearchResult[] = [];
  @Prop() searchQuery: string;
  @Prop() lastSearchQuery: string;
  @Prop() aiResultFragment: string = 'Ask AI to answer your question';
  @Prop() bielId: string;
  @Prop() stacked: boolean = false;
  @Prop() aiIcon: AiIconType = 'biel';
  @Prop() hideAvatars: boolean = false;
  @Prop() cleanTitles: boolean = false;
  @Prop() hideDescriptions: boolean = false;

  @State() selectedIndex: number = 0;
  @State() isHovering: boolean = false;
  @State() resultItems: HTMLElement[] = [];
  @State() isLoading: boolean = false;
  @State() hasSearched: boolean = false;

  @Event() resultClicked: EventEmitter<{ url: string; newTab: boolean }>;

  @Watch('searchResults')
  watchSearchResults() {
    // When search results change, we can mark search as completed
    if (this.isLoading) {
      this.isLoading = false;
      this.hasSearched = true;
    }
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (this.searchResults && this.searchResults.length > 0) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.navigateDown();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.navigateUp();
          break;
        case 'Enter':
          event.preventDefault();
          this.selectResult(event.ctrlKey || event.metaKey);
          break;
      }
    }
  }

  @Listen('inputChanged', { target: 'body' })
  handleInputChanged() {
    this.isLoading = true;
    this.hasSearched = false;
  }

  navigateDown() {
    const maxIndex = this.getTotalResultsCount();
    if (this.selectedIndex < maxIndex) {
      this.selectedIndex += 1;
      this.scrollToSelected();
    }
  }

  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex -= 1;
      this.scrollToSelected();
    }
  }

  getTotalResultsCount(): number {
    const aiButtonCount = this.hideAskAiButton ? 0 : 1;
    const resultsCount = this.searchResults ? this.searchResults.length : 0;

    // In toctree view, also count the toctree titles as selectable items
    if (this.shouldShowToctreeView()) {
      const groupedResults = this.groupResultsByPage();
      const toctreeTitlesCount = groupedResults.filter((group) => group.results.length > 1).length;
      return aiButtonCount + resultsCount + toctreeTitlesCount;
    }

    return aiButtonCount + resultsCount;
  }

  selectResult(newTab: boolean = false) {
    if (this.selectedIndex !== 0) {
      const selectedItem = this.getSelectedItem(this.selectedIndex);
      if (selectedItem) {
        this.resultClick(selectedItem.url, newTab);
      }
    }
  }

  // Function to get the selected item (could be a result or a toctree title)
  getSelectedItem(
    index: number
  ): { title: string; fragment: string; url: string } | { title: string; url: string } | null {
    if (this.shouldShowToctreeView()) {
      return this.getToctreeItemByIndex(index);
    } else {
      return this.getResultByIndex(index);
    }
  }

  // Function to get item by index in toctree view
  getToctreeItemByIndex(
    index: number
  ): { title: string; fragment: string; url: string } | { title: string; url: string } | null {
    if (!this.searchResults || this.searchResults.length === 0) {
      return null;
    }

    const aiButtonOffset = this.hideAskAiButton ? 0 : 1;
    const adjustedIndex = index - aiButtonOffset;

    if (adjustedIndex < 0) {
      return null;
    }

    const groupedResults = this.groupResultsByPage();
    let currentIndex = 0;

    for (const group of groupedResults) {
      // If group has only 1 result, it's rendered as normal item
      if (group.results.length === 1) {
        if (currentIndex === adjustedIndex) {
          return group.results[0];
        }
        currentIndex++;
      } else {
        // If group has multiple results, first index is the title
        if (currentIndex === adjustedIndex) {
          return { title: group.title, url: group.url };
        }
        currentIndex++;

        // Then the individual results
        for (const result of group.results) {
          if (currentIndex === adjustedIndex) {
            return result;
          }
          currentIndex++;
        }
      }
    }

    return null;
  }

  getResultByIndex(index: number): { title: string; fragment: string; url: string } | null {
    if (!this.searchResults || this.searchResults.length === 0) {
      return null;
    }

    const aiButtonOffset = this.hideAskAiButton ? 0 : 1;
    const resultIndex = index - aiButtonOffset;

    if (resultIndex >= 0 && resultIndex < this.searchResults.length) {
      return this.searchResults[resultIndex];
    }

    return null;
  }

  handleMouseEnter(index: number) {
    this.selectedIndex = index;
    this.isHovering = true;
  }

  handleMouseLeave() {
    this.selectedIndex = -1;
    this.isHovering = false;
  }

  scrollToSelected() {
    const selectedElement = this.resultItems[this.selectedIndex];
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  resultClick(url: string, newTab: boolean = false) {
    this.resultClicked.emit({ url: url, newTab });
  }

  getPageUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`;
    } catch {
      return url;
    }
  }

  groupResultsByPage(): GroupedResult[] {
    if (!this.searchResults || this.searchResults.length === 0) {
      return [];
    }

    const grouped = new Map<string, GroupedResult>();

    // First pass: group all results
    this.searchResults.forEach((result) => {
      let groupKey = result.page_title;

      // If no page_title, check if this result's title matches any other result's page_title
      if (!groupKey) {
        const matchingPageTitle = this.searchResults.find(
          (r) => r.page_title === result.title && r !== result
        )?.page_title;

        if (matchingPageTitle) {
          groupKey = matchingPageTitle;
        } else {
          groupKey = this.getPageUrl(result.url);
        }
      }

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          url: this.getPageUrl(result.url),
          title: groupKey.startsWith('http') ? result.title : groupKey, // Use groupKey as title unless it's a URL
          results: [],
        });
      }

      grouped.get(groupKey).results.push(result);
    });

    // Second pass: filter out redundant results
    grouped.forEach((group) => {
      // Remove results where title equals group title
      if (group.results.length > 1) {
        group.results = group.results.filter((result) => {
          // Keep results that have a different title from the group title
          return result.title !== group.title;
        });
      }
    });

    return Array.from(grouped.values()).filter((group) => group.results.length > 0);
  }

  // Function to clean titles by removing everything after "|" character
  cleanTitle(title: string): string {
    if (!this.cleanTitles || !title) return title;

    const pipeIndex = title.indexOf('|');
    if (pipeIndex === -1) return title;

    return title.substring(0, pipeIndex).trim();
  }

  // Function to highlight search query matches in text
  highlightMatch(text: string) {
    if (!this.searchQuery || !text) return text;

    // Escape special regex characters in the search query
    const escapedQuery = this.searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part) =>
      part.toLowerCase() === this.searchQuery.toLowerCase() ? (
        <mark class="biel-search-result__mark">{part}</mark>
      ) : (
        part
      )
    );
  }

  shouldShowToctreeView(): boolean {
    // If stacked mode is enabled, never use toctree view
    if (this.stacked) {
      return false;
    }

    // Only use toctree view if we have groups with multiple results
    const groupedResults = this.groupResultsByPage();
    const groupsWithMultipleResults = groupedResults.filter((group) => group.results.length > 1);

    const showToctree = groupsWithMultipleResults.length > 0;

    return showToctree;
  }

  render() {
    const showLoadingAnimation = this.isLoading;
    const showNoResults =
      this.hasSearched &&
      !this.isLoading &&
      this.searchResults &&
      this.searchResults.length === 0 &&
      this.searchQuery.length > 3;

    const showToctreeView = this.shouldShowToctreeView();
    const groupedResults = showToctreeView ? this.groupResultsByPage() : [];

    return (
      <div
        class="biel-search-body-container"
        part="container"
        onMouseLeave={() => this.handleMouseLeave()}
      >
        {this.searchQuery !== '' && (
          <div>
            <ul class="biel-search-body__result-list" part="result-list">
              {!this.hideAskAiButton && (
                <li
                  class="biel-search-body__result-item"
                  onMouseMove={() => this.handleMouseEnter(0)}
                  ref={(el) => (this.resultItems[0] = el as HTMLElement)}
                >
                  <biel-search-ask-ai-button
                    class="biel-search-body__result"
                    resultTitle={this.searchQuery}
                    resultFragment={this.aiResultFragment}
                    bielId={this.bielId}
                    isSelected={this.selectedIndex === 0}
                    aiIcon={this.aiIcon}
                    exportparts="link: biel-search-ask-ai-button-link, container: biel-search-ask-ai-button-container, icon: biel-search-ask-ai-button-icon, text-container: biel-search-ask-ai-button-text-container, title: biel-search-ask-ai-button-title, description: biel-search-ask-ai-button-description"
                  ></biel-search-ask-ai-button>
                </li>
              )}

              {showToctreeView &&
                (() => {
                  let globalIndex = this.hideAskAiButton ? 0 : 1;

                  return groupedResults.map((group, groupIndex) => {
                    // If group has only 1 result, render as individual result with page_title
                    if (group.results.length === 1) {
                      const result = group.results[0];
                      const currentGlobalIndex = globalIndex;
                      globalIndex++;

                      // Format title for single-item groups: "title - page_title" if both exist
                      const hasValidPageTitle =
                        result.page_title && result.page_title.trim().length > 0;
                      const cleanedTitle = this.cleanTitle(result.title);
                      const cleanedPageTitle = this.cleanTitle(result.page_title);
                      const displayTitle =
                        cleanedTitle && hasValidPageTitle
                          ? `${cleanedTitle} - ${cleanedPageTitle}`
                          : cleanedTitle;

                      return (
                        <li
                          class="biel-search-body__result-item"
                          key={`single-result-${groupIndex}`}
                          onMouseMove={() => this.handleMouseEnter(currentGlobalIndex)}
                          ref={(el) => (this.resultItems[currentGlobalIndex] = el as HTMLElement)}
                        >
                          <biel-search-result
                            class="biel-search-body__result"
                            onClick={(e: MouseEvent) =>
                              this.resultClick(result.url, e.ctrlKey || e.metaKey)
                            }
                            resultTitle={displayTitle}
                            resultFragment={result.fragment}
                            resultUrl={result.url}
                            searchQuery={this.searchQuery}
                            isSelected={this.selectedIndex === currentGlobalIndex}
                            hideDescription={this.hideDescriptions}
                            exportparts="link: biel-search-result-link, container: biel-search-result-container, title: biel-search-result-title, description: biel-search-result-description"
                          ></biel-search-result>
                        </li>
                      );
                    }

                    // If group has multiple results, render as toctree
                    const titleIndex = globalIndex;
                    globalIndex++;
                    const startIndex = globalIndex;
                    globalIndex += group.results.length;

                    return (
                      <li class="biel-search-body__result-group" key={`group-${groupIndex}`}>
                        <div
                          class={`biel-search-toctree-page-title ${this.selectedIndex === titleIndex ? 'selected' : ''}`}
                          onClick={(e: MouseEvent) =>
                            this.resultClick(group.url, e.ctrlKey || e.metaKey)
                          }
                          onMouseMove={() => this.handleMouseEnter(titleIndex)}
                          ref={(el) => (this.resultItems[titleIndex] = el as HTMLElement)}
                        >
                          <span class="biel-search-toctree-title">
                            {this.highlightMatch(this.cleanTitle(group.title))}
                          </span>
                          <span class="biel-search-toctree-count">
                            ({group.results.length} results)
                          </span>
                        </div>
                        <ul class="biel-search-toctree-items">
                          {group.results.map((result, resultIndex) => {
                            const currentGlobalIndex = startIndex + resultIndex;
                            const cleanedTitle = this.cleanTitle(result.title);
                            return (
                              <li
                                class="biel-search-body__result-item biel-search-toctree-item"
                                key={`result-${currentGlobalIndex}`}
                                onMouseMove={() => this.handleMouseEnter(currentGlobalIndex)}
                                ref={(el) =>
                                  (this.resultItems[currentGlobalIndex] = el as HTMLElement)
                                }
                              >
                                <biel-search-result
                                  class="biel-search-body__result biel-search-toctree-result"
                                  onClick={(e: MouseEvent) =>
                                    this.resultClick(result.url, e.ctrlKey || e.metaKey)
                                  }
                                  resultTitle={cleanedTitle}
                                  resultFragment={result.fragment}
                                  resultUrl={result.url}
                                  searchQuery={this.searchQuery}
                                  isSelected={this.selectedIndex === currentGlobalIndex}
                                  hideDescription={this.hideDescriptions}
                                  exportparts="link: biel-search-result-link, container: biel-search-result-container, title: biel-search-result-title, description: biel-search-result-description"
                                ></biel-search-result>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  });
                })()}

              {!showToctreeView &&
                this.searchResults &&
                this.searchResults.length > 0 &&
                this.searchResults.map((result, index) => {
                  // Title formatting based on mode
                  let displayTitle;
                  const hasValidPageTitle =
                    result.page_title && result.page_title.trim().length > 0;
                  const cleanedTitle = this.cleanTitle(result.title);
                  const cleanedPageTitle = this.cleanTitle(result.page_title);

                  if (this.stacked) {
                    // Stacked mode: "Title - Page Title" if both exist, otherwise just "Title"
                    displayTitle = result.page_title
                      ? `${cleanedTitle} - ${cleanedPageTitle}`
                      : cleanedTitle;
                  } else {
                    // Non-stacked mode: individual pages are "Title - Page Title"
                    displayTitle =
                      cleanedTitle && hasValidPageTitle
                        ? `${cleanedTitle} - ${cleanedPageTitle}`
                        : cleanedTitle;
                  }

                  return (
                    <li
                      class="biel-search-body__result-item"
                      onMouseMove={() => this.handleMouseEnter(index + 1)}
                      ref={(el) => (this.resultItems[index + 1] = el as HTMLElement)}
                    >
                      <biel-search-result
                        class="biel-search-body__result"
                        onClick={(e: MouseEvent) =>
                          this.resultClick(result.url, e.ctrlKey || e.metaKey)
                        }
                        resultTitle={displayTitle}
                        resultFragment={result.fragment}
                        resultUrl={result.url}
                        searchQuery={this.searchQuery}
                        isSelected={this.selectedIndex === index + 1}
                        hideDescription={this.hideDescriptions}
                        exportparts="link: biel-search-result-link, container: biel-search-result-container, title: biel-search-result-title, description: biel-search-result-description"
                      ></biel-search-result>
                    </li>
                  );
                })}
            </ul>
            {showLoadingAnimation && (
              <div class="loading-animation" part="loading">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
            )}
            {showNoResults && (
              <p class="biel-search-body-no-results" part="no-results">
                Nothing found for "{this.searchQuery}"
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
}
